/* Theme core: shared behaviour for every template.
   - Theme.toast(), Theme.cart (AJAX add / refresh), Theme.wishlist (localStorage), Theme.money()
   - Header: dropdown menus, mega menu, mobile drawer, predictive search
   - Theme.carousels: Owl Carousel presets + prev/next/pager controls driven by data attributes
   Everything is initialised once (guarded) and uses event delegation, so sections re-rendered by the
   theme editor do not accumulate duplicate listeners. */
(function () {
  'use strict';
  if (window.Theme && window.Theme.__core) return;
  var Theme = (window.Theme = window.Theme || {});
  Theme.__core = true;
  var S = function (k, fallback) { return (Theme.strings && Theme.strings[k]) || fallback || ''; };

  /* ---------- helpers ---------- */
  Theme.qs = function (sel, root) { return (root || document).querySelector(sel); };
  Theme.qsa = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  Theme.escape = function (str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  Theme.debounce = function (fn, wait) {
    var t;
    return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, wait); };
  };
  /* Formats an amount in minor units (cents) with the shop currency. */
  Theme.money = function (cents) {
    var cur = Theme.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };
  Theme.fetchJSON = function (url, opts) {
    return fetch(url, opts).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) { var err = new Error(data.description || data.message || r.statusText); err.data = data; throw err; }
        return data;
      });
    });
  };

  /* ---------- toast ---------- */
  var toastTimer;
  Theme.toast = function (message, opts) {
    var el = Theme.qs('[data-toast]');
    if (!el) return;
    opts = opts || {};
    Theme.qs('[data-toast-message]', el).textContent = message;
    var action = Theme.qs('[data-toast-action]', el);
    if (action) action.hidden = opts.action === false;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.hidden = true; }, 3200);
  };

  /* ---------- cart ---------- */
  Theme.cart = {
    state: null,
    setCount: function (n) {
      Theme.cartCount = n;
      Theme.qsa('[data-cart-count]').forEach(function (el) { el.textContent = n; });
    },
    refresh: function () {
      return Theme.fetchJSON('/cart.js', { headers: { Accept: 'application/json' } }).then(function (cart) {
        Theme.cart.state = cart;
        Theme.cart.setCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: { cart: cart } }));
        return cart;
      });
    },
    /* Adds items. `data` is a FormData / form element / plain object of cart/add params. */
    add: function (data) {
      var body;
      if (data instanceof HTMLFormElement) data = new FormData(data);
      if (data instanceof FormData) { body = data; }
      else { body = new FormData(); Object.keys(data).forEach(function (k) { body.append(k, data[k]); }); }
      return Theme.fetchJSON(Theme.routes.cartAdd + '.js', { method: 'POST', body: body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (item) { return Theme.cart.refresh().then(function (cart) { return { item: item, cart: cart }; }); });
    },
    change: function (lineOrKey, quantity, properties) {
      var payload = { quantity: quantity };
      if (typeof lineOrKey === 'number') payload.line = lineOrKey; else payload.id = lineOrKey;
      if (properties) payload.properties = properties;
      return Theme.fetchJSON(Theme.routes.cartChange + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
      }).then(function (cart) {
        Theme.cart.state = cart;
        Theme.cart.setCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: { cart: cart } }));
        return cart;
      });
    }
  };

  /* Any <form data-ajax-add> adds to cart without leaving the page. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ajax-add')) return;
    e.preventDefault();
    if (form.__busy) return;
    var btn = form.querySelector('[type="submit"]');
    var label = btn && (btn.getAttribute('data-add-label') || '');
    form.__busy = true;
    if (btn) btn.setAttribute('aria-busy', 'true');
    Theme.cart.add(form).then(function (res) {
      Theme.toast(S('addedToCart', 'Added to cart') + (label ? ': ' + label : ''));
      document.dispatchEvent(new CustomEvent('theme:cart:added', { detail: { form: form, item: res.item, cart: res.cart } }));
    }).catch(function (err) {
      Theme.toast(err.message || S('addError', 'Could not add to cart'), { action: false });
    }).then(function () {
      form.__busy = false;
      if (btn) btn.removeAttribute('aria-busy');
    });
  });

  /* ---------- wishlist (stored on the device) ---------- */
  var WL_KEY = 'theme.wishlist';
  var wl = {
    read: function () { try { var v = JSON.parse(localStorage.getItem(WL_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; } },
    write: function (list) { try { localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ } }
  };
  Theme.wishlist = {
    all: function () { return wl.read(); },
    has: function (handle) { return wl.read().indexOf(handle) > -1; },
    toggle: function (handle) {
      var list = wl.read(), i = list.indexOf(handle), added;
      if (i > -1) { list.splice(i, 1); added = false; } else { list.push(handle); added = true; }
      wl.write(list);
      Theme.wishlist.sync();
      document.dispatchEvent(new CustomEvent('theme:wishlist:changed', { detail: { handle: handle, added: added, list: list } }));
      return added;
    },
    remove: function (handle) { if (Theme.wishlist.has(handle)) Theme.wishlist.toggle(handle); },
    sync: function () {
      var list = wl.read();
      Theme.qsa('[data-wishlist-toggle]').forEach(function (btn) {
        var on = list.indexOf(btn.getAttribute('data-wishlist-toggle')) > -1;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var svg = btn.querySelector('svg');
        if (svg) { svg.setAttribute('fill', on ? '#A3471F' : 'none'); svg.setAttribute('stroke', on ? '#A3471F' : '#16181B'); }
      });
      Theme.qsa('[data-wishlist-count]').forEach(function (el) { el.textContent = list.length; });
    }
  };
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    var added = Theme.wishlist.toggle(btn.getAttribute('data-wishlist-toggle'));
    Theme.toast(added ? S('wishlistSaved', 'Saved to wishlist') : S('wishlistRemoved', 'Removed from wishlist'), { action: false });
  });
  window.addEventListener('storage', function (e) { if (e.key === WL_KEY) Theme.wishlist.sync(); });

  /* ---------- header: dropdown menus (support / account) ---------- */
  var menuTimer;
  function menuEl(name) { return Theme.qs('[data-menu="' + name + '"]'); }
  function setMenu(name, open) {
    var el = menuEl(name);
    if (!el) return;
    el.hidden = !open;
    Theme.qsa('[data-menu-trigger="' + name + '"]').forEach(function (t) { t.setAttribute('aria-expanded', open ? 'true' : 'false'); });
  }
  function closeMenus(except) {
    Theme.qsa('[data-menu]').forEach(function (m) { var n = m.getAttribute('data-menu'); if (n !== except) setMenu(n, false); });
  }
  var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-menu-trigger]');
    if (trigger) {
      var name = trigger.getAttribute('data-menu-trigger');
      var open = menuEl(name) && menuEl(name).hidden;
      closeMenus(name); closeSearch(); setMenu(name, !!open);
      return;
    }
    if (!(e.target.closest && e.target.closest('[data-menu]'))) closeMenus();
  });
  if (canHover) {
    ['mouseover', 'mouseout'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var host = e.target.closest && e.target.closest('[data-menu-trigger], [data-menu]');
        if (!host) return;
        var name = host.getAttribute('data-menu-trigger') || host.getAttribute('data-menu');
        clearTimeout(menuTimer);
        if (evt === 'mouseover') { closeMenus(name); closeSearch(); setMenu(name, true); }
        else menuTimer = setTimeout(function () { setMenu(name, false); }, 200);
      });
    });
  }

  /* ---------- header: mega menu ---------- */
  var megaTimer, megaOpenKey = '';
  function setMega(key) {
    megaOpenKey = key || '';
    Theme.qsa('[data-mega-panel]').forEach(function (p) { p.hidden = p.getAttribute('data-mega-panel') !== megaOpenKey; });
    Theme.qsa('[data-mega-trigger]').forEach(function (t) {
      var on = t.getAttribute('data-mega-trigger') === megaOpenKey;
      t.setAttribute('aria-expanded', on ? 'true' : 'false');
      var chev = t.querySelector('.hd-chev');
      if (chev) chev.classList.toggle('is-open', on);
    });
  }
  document.addEventListener('mouseover', function (e) {
    var root = e.target.closest && e.target.closest('[data-mega-root]');
    var trigger = e.target.closest && e.target.closest('[data-mega-trigger]');
    var panel = e.target.closest && e.target.closest('[data-mega-panel] .mega-menu__panel');
    var item = e.target.closest && e.target.closest('.category-nav__item');
    if (!root) return;
    clearTimeout(megaTimer);
    if (panel) return;
    var key = trigger ? trigger.getAttribute('data-mega-trigger') : (item ? '' : megaOpenKey);
    if (e.target.closest('[data-mega-close]')) { setMega(''); return; }
    if (key === megaOpenKey) return;
    megaTimer = setTimeout(function () { setMega(key); }, megaOpenKey ? 60 : 150);
  });
  document.addEventListener('mouseout', function (e) {
    if (!(e.target.closest && e.target.closest('[data-mega-root]'))) return;
    if (e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('[data-mega-root]')) return;
    clearTimeout(megaTimer);
    megaTimer = setTimeout(function () { setMega(''); }, 200);
  });
  document.addEventListener('focusin', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-mega-trigger]');
    if (trigger) setMega(trigger.getAttribute('data-mega-trigger'));
    else if (megaOpenKey && !(e.target.closest && e.target.closest('[data-mega-root]'))) setMega('');
  });
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-mega-trigger]');
    if (trigger) {
      var key = trigger.getAttribute('data-mega-trigger');
      /* tap on touch devices / click: first activation opens the panel, second follows the link */
      if (megaOpenKey !== key) { e.preventDefault(); setMega(key); }
      return;
    }
    if (e.target.closest && e.target.closest('[data-mega-close]')) setMega('');
  });

  /* ---------- header: mobile drawer ---------- */
  function setDrawer(open) {
    var d = Theme.qs('[data-drawer]');
    if (!d) return;
    d.hidden = !open;
    Theme.qsa('.site-header__menu-button[data-drawer-toggle]').forEach(function (b) { b.setAttribute('aria-expanded', open ? 'true' : 'false'); });
    document.body.style.overflow = open ? 'hidden' : '';
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-drawer-toggle]')) {
      var d = Theme.qs('[data-drawer]');
      setDrawer(d ? d.hidden : false);
    }
  });

  /* ---------- header: predictive search ---------- */
  var searchEls = null, searchAbort = null, searchBlur;
  function se() {
    if (searchEls) return searchEls;
    var form = Theme.qs('[data-site-search]');
    if (!form) return null;
    searchEls = {
      form: form, input: Theme.qs('[data-search-input]', form), panel: Theme.qs('[data-search-panel]', form),
      trending: Theme.qs('[data-search-trending]', form), suggestions: Theme.qs('[data-search-suggestions]', form),
      productsWrap: Theme.qs('[data-search-products-wrap]', form), products: Theme.qs('[data-search-products]', form),
      empty: Theme.qs('[data-search-empty]', form)
    };
    return searchEls;
  }
  function openSearch() {
    var s = se(); if (!s) return;
    closeMenus();
    s.panel.hidden = false; s.input.setAttribute('aria-expanded', 'true');
  }
  function closeSearch() {
    var s = se(); if (!s) return;
    s.panel.hidden = true; s.input.setAttribute('aria-expanded', 'false');
  }
  function runSearch() {
    var s = se(); if (!s) return;
    var q = s.input.value.trim();
    if (s.trending) s.trending.hidden = !!q;
    if (!q) {
      s.suggestions.innerHTML = ''; s.products.innerHTML = ''; s.productsWrap.hidden = true; s.empty.hidden = true;
      return;
    }
    if (searchAbort) searchAbort.abort();
    searchAbort = window.AbortController ? new AbortController() : null;
    var url = Theme.routes.predictiveSearch + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product,query&resources[limit]=4&resources[options][unavailable_products]=last';
    fetch(url, { signal: searchAbort && searchAbort.signal, headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (data) {
        var res = (data.resources && data.resources.results) || {};
        var queries = res.queries || [], products = res.products || [];
        s.suggestions.innerHTML = queries.map(function (qr) {
          return '<button class="search-panel__suggestion" type="button" data-search-term="' + Theme.escape(qr.text) + '"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B6168" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-3.5-3.5"></path></svg>' + Theme.escape(qr.text) + '</button>';
        }).join('');
        s.products.innerHTML = products.map(function (p) {
          var img = p.featured_image && p.featured_image.url ? '<img class="search-panel__product-image" src="' + Theme.escape(p.featured_image.url) + '" alt="" width="48" height="48" loading="lazy">' : '';
          return '<a class="search-panel__product" href="' + Theme.escape(p.url) + '">' + img + '<span class="search-panel__product-name">' + Theme.escape(p.title) + '</span><span class="search-panel__product-price">' + Theme.escape(p.price ? Theme.money(Math.round(parseFloat(p.price) * 100)) : '') + '</span></a>';
        }).join('');
        s.productsWrap.hidden = products.length === 0;
        s.empty.hidden = !(products.length === 0 && queries.length === 0);
        if (!s.empty.hidden) s.empty.textContent = S('noMatches', 'No matches').replace('__TERMS__', q);
      })
      .catch(function (err) { if (err && err.name === 'AbortError') return; });
  }
  document.addEventListener('focusin', function (e) {
    if (e.target.matches && e.target.matches('[data-search-input]')) { clearTimeout(searchBlur); openSearch(); runSearch(); }
  });
  document.addEventListener('input', function (e) {
    if (e.target.matches && e.target.matches('[data-search-input]')) { openSearch(); Theme.debounce(runSearch, 200)(); }
  });
  document.addEventListener('focusout', function (e) {
    var s = se();
    if (s && e.target === s.input) searchBlur = setTimeout(function () { if (!s.form.contains(document.activeElement)) closeSearch(); }, 200);
  });
  document.addEventListener('click', function (e) {
    var termBtn = e.target.closest && e.target.closest('[data-search-term]');
    var s = se();
    if (termBtn && s) {
      e.preventDefault();
      s.input.value = termBtn.getAttribute('data-search-term');
      s.input.focus(); runSearch();
      return;
    }
    if (s && !(e.target.closest && e.target.closest('[data-site-search]'))) closeSearch();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    closeSearch(); closeMenus(); setMega('');
    var d = Theme.qs('[data-drawer]');
    if (d && !d.hidden) setDrawer(false);
  });

  /* ---------- carousels (Owl Carousel) ----------
     Markup contract:
       <ul id="x" class="owl-carousel" data-carousel="rail|peek|category" [data-autoplay="true"]>
       [data-carousel-prev="#x"] [data-carousel-next="#x"]        buttons
       [data-carousel-pager="#x"]                                  receives one dot per page
       [data-carousel-counter="#x"]                                receives "1 / 3"
       [data-carousel-goto="#x"][data-index="2"]                   explicit slide buttons (is-on toggled on .hm-dot)
     Presets mirror the prototype's Owl configuration. */
  var PRESETS = {
    rail: {
      nav: false, dots: false, rewind: false, slideBy: 'page', margin: 24, items: 4, smartSpeed: 450, touchDrag: false,
      responsive: {
        0: { items: 1, autoWidth: true, slideBy: 1, margin: 12, stagePadding: 0, dots: true },
        576: { items: 2, autoWidth: false, dots: false, slideBy: 'page', stagePadding: 0, margin: 20 },
        768: { items: 3, margin: 24 }, 992: { items: 3 }, 1200: { items: 4 }, 1400: { items: 4 }
      }
    },
    peek: {
      items: 1, autoWidth: true, center: true, loop: true, margin: 24, nav: false, dots: false, smartSpeed: 400, mouseDrag: true, touchDrag: false,
      autoplayTimeout: 6000, autoplayHoverPause: true, responsive: { 0: { margin: 12 }, 768: { margin: 24 } }
    },
    category: {
      nav: false, dots: false, rewind: false, slideBy: 1, margin: 16, items: 7, smartSpeed: 450, touchDrag: false,
      responsive: {
        0: { items: 1, autoWidth: true, margin: 8, dots: true }, 576: { items: 3, autoWidth: false, margin: 16, dots: false },
        768: { items: 4 }, 992: { items: 5 }, 1200: { items: 6 }, 1400: { items: 7 }
      }
    }
  };
  function assign(a, b) { for (var k in b) if (Object.prototype.hasOwnProperty.call(b, k)) a[k] = b[k]; return a; }
  function ctl(attr, id) { return Theme.qsa('[' + attr + '="#' + id + '"]'); }

  function owlInfo($el) {
    var d = $el.data('owl.carousel');
    if (!d) return null;
    var n = d.items().length, cur = d.current();
    var i = cur === null || cur === undefined ? 0 : d.relative(cur);
    var size = Math.max(1, Math.floor(d.settings.items || 1));
    var pages = Math.max(1, Math.ceil(n / size));
    var page = i >= n - size ? pages - 1 : Math.floor(i / size);
    return { i: i, n: n, size: size, page: page, pages: pages, loop: !!d.settings.loop };
  }
  function syncControls($el, kind) {
    var id = $el.attr('id'), info = owlInfo($el);
    if (!id || !info) return;
    ctl('data-carousel-prev', id).forEach(function (b) { b.disabled = !info.loop && info.i <= 0; });
    ctl('data-carousel-next', id).forEach(function (b) { b.disabled = !info.loop && info.i >= info.n - info.size; });
    ctl('data-carousel-counter', id).forEach(function (c) { c.textContent = (info.page + 1) + ' / ' + info.pages; });
    ctl('data-carousel-pager', id).forEach(function (p) {
      if (p.children.length !== info.pages) {
        var label = p.getAttribute('data-label') || 'Page';
        p.innerHTML = '';
        for (var k = 0; k < info.pages; k++) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'pager__dot'; b.setAttribute('data-page', k);
          b.setAttribute('aria-label', label + ' ' + (k + 1) + ' / ' + info.pages);
          b.innerHTML = '<span class="hm-dot"></span>';
          p.appendChild(b);
        }
      }
      Array.prototype.forEach.call(p.children, function (b, k) {
        var on = k === info.page;
        b.setAttribute('aria-current', on ? 'true' : 'false');
        var dot = b.querySelector('.hm-dot'); if (dot) dot.classList.toggle('is-on', on);
      });
    });
    ctl('data-carousel-goto', id).forEach(function (b) {
      var on = parseInt(b.getAttribute('data-index'), 10) === info.i;
      b.setAttribute('aria-current', on ? 'true' : 'false');
      var dot = b.querySelector('.hm-dot'); if (dot) dot.classList.toggle('is-on', on);
    });
    /* Peek carousel: highlight the active card */
    if (kind === 'peek') {
      $el.find('.owl-item').each(function () {
        var c = this.querySelector('.pk-card'); if (c) c.classList.toggle('is-on', this.classList.contains('center'));
      });
    }
  }
  /* One swipe = exactly one slide. */
  function bindSwipe($el, loop) {
    var node = $el[0], sx = 0, sy = 0;
    node.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
    node.addEventListener('touchend', function (e) {
      var t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      var dir = dx < 0 ? 1 : -1;
      if (loop) { $el.trigger((dir > 0 ? 'next' : 'prev') + '.owl.carousel', [400]); return; }
      var d = $el.data('owl.carousel'); if (!d) return;
      var target = Math.max(0, Math.min(d.maximum(), d.relative(d.current()) + dir));
      $el.trigger('to.owl.carousel', [target, 400, true]);
    }, { passive: true });
  }
  Theme.carousels = {
    init: function (root) {
      var $ = window.jQuery;
      if (!$ || !$.fn || !$.fn.owlCarousel) return;
      Theme.qsa('[data-carousel]', root).forEach(function (el) {
        var $el = $(el);
        if ($el.hasClass('owl-loaded') || el.__carouselInit) return;
        el.__carouselInit = true;
        var kind = el.getAttribute('data-carousel');
        var cfg = assign({}, PRESETS[kind] || PRESETS.rail);
        if (kind === 'peek') {
          var reduce = false;
          try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* ignore */ }
          cfg.autoplay = el.getAttribute('data-autoplay') === 'true' && !reduce;
        }
        $el.on('initialized.owl.carousel changed.owl.carousel resized.owl.carousel refreshed.owl.carousel', function () { setTimeout(function () { syncControls($el, kind); }, 0); });
        $el.owlCarousel(cfg);
        bindSwipe($el, kind === 'peek');
        if (kind === 'peek') {
          /* side slides bring themselves to the centre when clicked */
          $el.on('click', '.owl-item:not(.center)', function () {
            var d = $el.data('owl.carousel'); $el.trigger('to.owl.carousel', [d.relative($(this).index()), 350, true]);
          });
        }
      });
    },
    go: function (id, index) { window.jQuery('#' + id).trigger('to.owl.carousel', [index, 350, true]); },
    destroy: function (root) {
      var $ = window.jQuery; if (!$) return;
      Theme.qsa('[data-carousel]', root).forEach(function (el) {
        var $el = $(el);
        if ($el.hasClass('owl-loaded')) { $el.off('.owl.carousel'); $el.trigger('destroy.owl.carousel'); }
        el.__carouselInit = false;
      });
    }
  };
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-carousel-prev],[data-carousel-next],[data-carousel-goto],[data-page]');
    if (!t || !window.jQuery) return;
    var $ = window.jQuery, id, sel;
    if ((sel = t.getAttribute('data-carousel-prev'))) $(sel).trigger('prev.owl.carousel', [350]);
    else if ((sel = t.getAttribute('data-carousel-next'))) $(sel).trigger('next.owl.carousel', [350]);
    else if ((sel = t.getAttribute('data-carousel-goto'))) $(sel).trigger('to.owl.carousel', [parseInt(t.getAttribute('data-index'), 10) || 0, 350, true]);
    else if (t.hasAttribute('data-page') && (id = t.parentNode.getAttribute('data-carousel-pager'))) {
      var d = $(id).data('owl.carousel'), size = Math.max(1, Math.floor((d && d.settings.items) || 1));
      $(id).trigger('to.owl.carousel', [parseInt(t.getAttribute('data-page'), 10) * size, 350, true]);
    }
  });

  /* ---------- boot ---------- */
  function boot() {
    Theme.wishlist.sync();
    Theme.carousels.init(document);
    /* keep the badge right when the page was restored from bfcache */
    window.addEventListener('pageshow', function (e) { if (e.persisted) Theme.cart.refresh().catch(function () {}); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  /* Theme editor: re-init when a section is (re)loaded */
  document.addEventListener('shopify:section:load', function (e) { Theme.carousels.init(e.target); Theme.wishlist.sync(); });
  document.addEventListener('shopify:section:unload', function (e) { Theme.carousels.destroy(e.target); });
})();
